import React from 'react';
import { useKeycloakAuth } from '@/hooks/useKeycloakAuth';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { MaintenanceMode } from '@/components/MaintenanceMode';
import { RedirectToKeycloakLogin } from '@/components/RedirectToKeycloakLogin';
import { isInMaintenanceMode, MAINTENANCE_TITLE, MAINTENANCE_DESCRIPTION } from '@/config/maintenance';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface JadlogRouteProps {
  children: React.ReactNode;
}

// Generator ID da Jadlog (UUID legacy no Supabase)
export const JADLOG_GENERATOR_ID = '79aa465d-fa35-46a8-9d0c-e4603ac6e812';

export const JadlogRoute = ({ children }: JadlogRouteProps) => {
  const { isAuthenticated, isLoading: authLoading } = useKeycloakAuth();
  const { profile, isLoading: profileLoading } = useUnifiedAuth();

  // Verificar se o generator_id do usuário pertence à árvore Jadlog (raiz 3775)
  const generatorId = profile?.generator_id ?? null;
  const isInternal = profile?.is_internal === true;

  const { data: isJadlogGenerator, isLoading: isCheckingHierarchy } = useQuery({
    queryKey: ['is-jadlog-generator', generatorId],
    queryFn: async () => {
      if (!generatorId) return false;
      const { data, error } = await (supabase.rpc as any)(
        'is_jadlog_generator',
        { p_generator_id: generatorId }
      );
      if (error) {
        console.error('❌ Error checking Jadlog hierarchy:', error);
        return false;
      }
      return data === true;
    },
    enabled: !!generatorId && !isInternal,
    staleTime: 10 * 60 * 1000,
  });

  const isLoading = authLoading || profileLoading || (!isInternal && !!generatorId && isCheckingHierarchy);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Verificar se o usuário está autenticado
  if (!isAuthenticated) {
    return <RedirectToKeycloakLogin />;
  }

  // Verificar modo de manutenção
  if (isInMaintenanceMode(profile?.generator_id_legacy)) {
    return <MaintenanceMode title={MAINTENANCE_TITLE} description={MAINTENANCE_DESCRIPTION} />;
  }

  // Controle de acesso: internos sempre têm acesso; externos precisam pertencer à árvore Jadlog
  if (!isInternal && !isJadlogGenerator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md px-4">
          <h1 className="text-2xl font-bold text-foreground">Acesso Negado</h1>
          <p className="text-muted-foreground">
            Você não tem permissão para acessar esta página. Esta área é restrita ao gerador Jadlog e suas unidades.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
